import { z } from 'zod';
/**
 * Arbeitszeiten (Modul `payroll`): Stunden je Mitarbeiter und Tag, von Hand erfasst. Die Rechnung hier gilt auch für die Schichten der Schichtplanung, damit beide Quellen
 * Nacht- und Sonntagsstunden gleich zählen. Nachtarbeit ist die Zeit von 20 bis 6 Uhr, Sonntagsarbeit die Zeit zwischen 0 und 24 Uhr am Sonntag. Feiertage gibt der Benutzer im Lauf an.
 * Hinweise zur Pause und zur Tageshöchstzeit folgen dem Arbeitszeitgesetz (§ 3 und § 4), sie sind Warnungen und verhindern das Speichern nicht.
 */
const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;
const DATE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
export const workEntryInput = z
    .object({
    employeeId: z.uuid('Bitte einen Mitarbeiter wählen'),
    workDate: z.string().regex(DATE, 'Bitte ein Datum angeben'),
    startTime: z.string().regex(TIME, 'Bitte eine Uhrzeit wie 08:00 angeben'),
    endTime: z.string().regex(TIME, 'Bitte eine Uhrzeit wie 16:30 angeben'),
    breakMinutes: z.number('Bitte eine Zahl angeben').int('Ganze Minuten').min(0).max(600).default(0),
    note: z.string().trim().max(200).nullable().optional().transform((v) => (v ? v : null)),
})
    .strict()
    .refine((v) => v.startTime !== v.endTime, { path: ['endTime'], message: 'Beginn und Ende sind gleich' })
    .refine((v) => workBreakdown(v).minutes > 0, { path: ['breakMinutes'], message: 'Die Pause ist so lang wie die ganze Arbeitszeit' });
export const updateWorkEntryInput = z
    .object({
    workDate: z.string().regex(DATE).optional(),
    startTime: z.string().regex(TIME).optional(),
    endTime: z.string().regex(TIME).optional(),
    breakMinutes: z.number().int().min(0).max(600).optional(),
    note: z.string().trim().max(200).nullable().optional().transform((v) => (v === undefined ? undefined : v ? v : null)),
})
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Keine Änderung angegeben');
const toMinutes = (t) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
const overlap = (a0, a1, b0, b1) => Math.max(0, Math.min(a1, b1) - Math.max(a0, b0));
const dayNumber = (iso) => Math.floor(Date.UTC(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10))) / 86_400_000);
/**
 * Arbeitszeit eines Tages in Minuten, mit Nacht- und Sonntagsanteil. Liegt das Ende vor dem Beginn, reicht die Arbeit über Mitternacht. Die Pause mindert die Arbeitszeit und
 * die Zuschlagszeiten im gleichen Verhältnis, weil sich nicht sagen lässt, in welchen Stunden pausiert wurde.
 */
export function workBreakdown(d) {
    const start = toMinutes(d.startTime);
    let end = toMinutes(d.endTime);
    const overnight = end <= start;
    if (overnight)
        end += 1440;
    const span = end - start;
    const total = Math.max(0, span - (d.breakMinutes ?? 0));
    // 0 bis 6 Uhr, 20 bis 6 Uhr des Folgetags (20 bis 30 Uhr) und 20 Uhr des Folgetags (44 Uhr) bis 6 Uhr des übernächsten Tags
    const night = overlap(start, end, 0, 360) + overlap(start, end, 1200, 1800) + overlap(start, end, 2640, 3240);
    // 1970-01-01 war ein Donnerstag: (Tage + 4) mod 7 ergibt 0 für Sonntag
    const weekday = (dayNumber(d.workDate) + 4) % 7;
    const sunday = weekday === 0 ? overlap(start, end, 0, 1440) : weekday === 6 ? overlap(start, end, 1440, 2880) : 0;
    return { minutes: total, nightMinutes: Math.round((night * total) / span), sundayMinutes: Math.round((sunday * total) / span), overnight };
}
/** Zeitspanne eines Eintrags auf einer durchgehenden Minutenachse, für die Prüfung auf Überschneidung */
export function workRange(d) {
    const base = dayNumber(d.workDate) * 1440;
    const start = base + toMinutes(d.startTime);
    let end = base + toMinutes(d.endTime);
    if (end <= start)
        end += 1440;
    return [start, end];
}
/** Hinweise zu einem Tag nach dem Arbeitszeitgesetz. Die Pause richtet sich nach der Arbeitszeit ohne Pause. */
export function workWarnings(d, minutes) {
    const out = [];
    if (minutes > 600)
        out.push('Mehr als zehn Stunden am Tag: Das Arbeitszeitgesetz erlaubt das nur im Ausnahmefall.');
    const gross = minutes + d.breakMinutes;
    if (gross > 9 * 60 && d.breakMinutes < 45)
        out.push('Bei mehr als neun Stunden sind mindestens 45 Minuten Pause vorgeschrieben.');
    else if (gross > 6 * 60 && d.breakMinutes < 30)
        out.push('Bei mehr als sechs Stunden sind mindestens 30 Minuten Pause vorgeschrieben.');
    return out;
}
/** Ruhezeit zwischen zwei Arbeitstagen in Minuten, kürzer als elf Stunden ist nach § 5 ArbZG zu wenig */
export const MIN_REST_MINUTES = 11 * 60;
//# sourceMappingURL=hours.js.map